
import React, { useState } from 'react';
import { ReviewItem, AIEvent, AppConfig, Theme } from '../types';
import { storageService } from '../services/storageService';
import { googleSheetsService } from '../services/googleSheetsService';
import { supabaseService } from '../services/supabaseService';
import { CATEGORIES, PRIORITY_LEVELS } from '../constants';

interface ReviewQueueProps {
  queue: ReviewItem[];
  config: AppConfig;
  onUpdate: () => void;
  onEventsApproved: () => void;
  theme: Theme;
}

const ReviewQueue: React.FC<ReviewQueueProps> = ({ queue, config, onUpdate, onEventsApproved, theme }) => {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [localEdits, setLocalEdits] = useState<Record<string, Partial<AIEvent>>>({});

  const pendingItems = queue.filter(item => item.status === 'PENDING');

  const updateLocalItem = (eventId: string, field: keyof AIEvent, value: any) => {
    setLocalEdits(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], [field]: value }
    }));
  };

  const handleAction = async (item: ReviewItem, action: 'APPROVE') => {
    setProcessingId(item.event.id);
    try {
      const finalEvent = {
        ...item.event,
        ...(localEdits[item.event.id] || {})
      };

      if (supabaseService.isConfigured(config)) {
        await supabaseService.insertEvent(config, finalEvent);
      } else if (googleSheetsService.isConnected()) {
        await googleSheetsService.appendEvent(config, finalEvent);
      } else {
        storageService.addEvent(finalEvent);
      }
      onEventsApproved();
      
      const newQueue = queue.map(q => {
        if (q.event.id === item.event.id) {
          return { ...q, status: 'APPROVED' as const };
        }
        return q;
      });
      
      storageService.saveReviewQueue(newQueue);
      onUpdate();
    } catch (err: any) {
      console.error(err);
      alert(`Sync Error: ${err.message || 'The cloud database rejected this record.'}`);
    } finally {
      setProcessingId(null);
    }
  };

  const selectClassName = `w-full text-[11px] p-3 rounded-lg border bg-transparent focus:ring-1 focus:ring-accent appearance-none cursor-pointer transition-all ${
    theme === 'dark' ? 'border-white/10 text-white bg-black/40' : 'border-black/10 text-slate-900 bg-white shadow-sm'
  }`;

  const inputClassName = `w-full text-[11px] p-3 rounded-lg border bg-transparent focus:ring-1 focus:ring-accent transition-all ${
    theme === 'dark' ? 'border-white/10 text-white bg-black/40 placeholder:text-white/20' : 'border-black/10 text-slate-900 bg-white shadow-sm placeholder:text-slate-400'
  }`;

  const optionClassName = theme === 'dark' ? 'bg-[#0a0a0a] text-white' : 'bg-white text-slate-900';

  if (pendingItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center animate-in fade-in duration-500">
        <div className={`w-20 h-20 rounded-[2.5rem] flex items-center justify-center mb-8 border ${
          theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-black/5'
        }`}>
          <svg className="w-10 h-10 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Review Queue Clear</h2>
        <p className="text-slate-400 dark:text-white/40 max-w-sm">The queue is empty. Launch the Discovery Engine to find new intelligence leads.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-32 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="space-y-4">
        {pendingItems.map((item) => {
          const edits = localEdits[item.event.id] || {};
          const currentCategory = edits.category || item.event.category;
          const currentPriority = edits.priority || item.event.priority;
          const currentSize = edits.attendeeSize !== undefined ? edits.attendeeSize : (item.event.attendeeSize || '');
          const currentTarget = edits.targetAudience !== undefined ? edits.targetAudience : (item.event.targetAudience || '');
          const isCurrentProcessing = processingId === item.event.id;

          return (
            <div key={item.event.id} className={`relative border rounded-3xl overflow-hidden transition-all ${
              theme === 'dark' ? 'bg-[#0a0a0a] border-white/10' : 'bg-white border-black/5 shadow-sm'
            } ${isCurrentProcessing ? 'opacity-70 pointer-events-none' : ''}`}>
              
              <div className="p-8 pb-4 flex justify-between items-start">
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent font-bold text-lg">
                    {item.event.campaignName.charAt(0)}
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{item.event.campaignName}</h3>
                    <p className="text-[10px] text-slate-400 dark:text-white/30 uppercase tracking-[0.2em] font-bold mt-1">
                      {item.event.location} • {item.event.startDate}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <button 
                    disabled={isCurrentProcessing}
                    onClick={() => handleAction(item, 'APPROVE')} 
                    className={`min-w-[180px] h-[50px] flex items-center justify-center gap-3 text-[11px] font-extrabold uppercase tracking-widest rounded-xl shadow-lg transition-all ${
                      isCurrentProcessing 
                        ? 'bg-accent/20 text-accent cursor-wait' 
                        : 'bg-accent text-black hover:brightness-110 active:scale-95 shadow-accent/10'
                    }`}
                  >
                    {isCurrentProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-accent/20 border-t-accent rounded-full animate-spin"></div>
                        <span>Syncing...</span>
                      </>
                    ) : (
                      'Approve & Save'
                    )}
                  </button>
                </div>
              </div>

              <div className="p-8 pt-2 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8 space-y-6">
                   <div className="space-y-3">
                     <span className="text-[9px] uppercase font-bold text-slate-400 dark:text-white/40 tracking-[0.2em]">AI Extraction Summary</span>
                     <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-white/70' : 'text-slate-600'}`}>
                       "{item.event.focusArea}"
                     </p>
                   </div>
                   
                   <div className={`p-4 rounded-xl text-xs font-mono transition-colors ${theme === 'dark' ? 'bg-black/40 text-white/30' : 'bg-slate-50 text-slate-400'}`}>
                     Reference: <a href={item.event.url} target="_blank" rel="noopener noreferrer" className="text-accent underline hover:opacity-80 transition-opacity">{item.event.url}</a>
                   </div>
                </div>

                <div className="lg:col-span-4">
                  <div className={`p-6 rounded-2xl border space-y-5 ${theme === 'dark' ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-black/5'}`}>
                    
                    {/* Category & Priority */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[9px] uppercase font-bold text-slate-400 dark:text-white/30 tracking-widest block">CATEGORY</label>
                        <select 
                          className={selectClassName}
                          value={currentCategory}
                          onChange={(e) => updateLocalItem(item.event.id, 'category', e.target.value)}
                        >
                          {CATEGORIES.map(c => <option key={c} value={c} className={optionClassName}>{c}</option>)}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] uppercase font-bold text-slate-400 dark:text-white/30 tracking-widest block">PRIORITY</label>
                        <select 
                          className={selectClassName}
                          value={currentPriority}
                          onChange={(e) => updateLocalItem(item.event.id, 'priority', e.target.value)}
                        >
                          <option value="" className={optionClassName}>Rank...</option>
                          {PRIORITY_LEVELS.map(p => <option key={p} value={p} className={optionClassName}>{p}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* New Audience Fields */}
                    <div className="space-y-2">
                      <label className="text-[9px] uppercase font-bold text-slate-400 dark:text-white/30 tracking-widest block">AUDIENCE SIZE</label>
                      <input 
                        className={inputClassName}
                        value={currentSize}
                        onChange={(e) => updateLocalItem(item.event.id, 'attendeeSize', e.target.value)}
                        placeholder="e.g. 5000+"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] uppercase font-bold text-slate-400 dark:text-white/30 tracking-widest block">TARGET AUDIENCE</label>
                      <input 
                        className={inputClassName}
                        value={currentTarget}
                        onChange={(e) => updateLocalItem(item.event.id, 'targetAudience', e.target.value)}
                        placeholder="e.g. Developers, C-Suite"
                      />
                    </div>

                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReviewQueue;
